import { Color as ColorVariable } from '@persona-id/design-tokens';
import Color from 'color';
import { memoize } from 'lodash';
import { isEqual, maxBy } from 'lodash/fp';
import { MutableRefObject, ReactElement, useMemo, useRef } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { boundingBoxArea, boundingBoxesOverlap } from 'dashboard/lib/utils/boundingBox';
import { matchLevelSatisfies } from 'dashboard/lib/utils/matchLevel';
import { ExtractionType } from 'dashboard/models/document';
import {
  DocumentViewerSingletonExtraction,
  ExtractionGroup,
  FocusedExtraction,
} from 'dashboard/redux/reducers/document-viewer-document-generic';
import ExtractionHoverCard from 'dashboard/views/DashboardDocumentViewerView/ExtractionHoverCard';
import OverlappingExtractionHoverCard from 'dashboard/views/DashboardDocumentViewerView/OverlappingExtractionsHoverCard';
import Tooltip from 'design-system/components/Tooltip';
import defaultTheme from 'design-system/theme';

export enum ExtractionColor {
  Unguided = 'yellow',
  GuidedSatisfied = 'green',
  GuidedUnsatisfied = 'red',
}

interface HighlightAnnotationProps {
  fillHighlight: boolean;
  color: ExtractionColor;
}

const HighlightAnnotation = styled('div')<HighlightAnnotationProps>`
  background: ${({ fillHighlight, color }) =>
    fillHighlight &&
    (color === ExtractionColor.Unguided
      ? Color(ColorVariable.CoreYellow400).alpha(0.5).string()
      : color === ExtractionColor.GuidedSatisfied
        ? Color(ColorVariable.CoreGreen500).alpha(0.5).string()
        : Color(ColorVariable.CoreRed700).alpha(0.5).string())};
  outline: 2px solid;
  outline-color: ${({ color }) =>
    color === ExtractionColor.Unguided
      ? Color(ColorVariable.CoreYellow600).alpha(0.7).string()
      : color === ExtractionColor.GuidedSatisfied
        ? Color(ColorVariable.CoreGreen600).alpha(0.7).string()
        : Color(ColorVariable.CoreRed800).alpha(0.7).string()};
  border-radius: 4px;
  mix-blend-mode: darken;
  cursor: pointer;
`;

const extractionsMatchFocusedExtractions = memoize(
  (focusedExtractions: FocusedExtraction[], extractions: DocumentViewerSingletonExtraction[]) =>
    isEqual(
      focusedExtractions.map((e) => e.extractionGroup),
      extractions.map((e) => e.extractionGroup),
    ) &&
    isEqual(
      focusedExtractions.map((e) => e.focusedBoundingBoxId),
      extractions.map((e) => e.result.id),
    ),
  (focusedExtractions, extractions) => JSON.stringify([focusedExtractions, extractions]),
);

export const getExtractionHighlightId = (extractionGroup: ExtractionGroup, extractionId: number) =>
  `document-highlight-${extractionGroup.fieldName}-${extractionGroup.extractionValue}-${extractionId}`;

interface OverlappingExtractionGroupHighlightProps {
  overlappingExtractions: DocumentViewerSingletonExtraction[];
  focusedExtractions: FocusedExtraction[];
  hoveredExtractions: FocusedExtraction[];
  setFocusedExtraction: (focusedExtractions: FocusedExtraction[]) => void;
  setHoveredExtraction: (hoveredExtractions: FocusedExtraction[]) => void;
  removeFocusedExtraction: () => void;
  removeHoveredExtraction: () => void;
  documentFileContainerRef: MutableRefObject<HTMLDivElement>;
  hideFill?: boolean;
}

const OverlappingExtractionGroupHighlight = ({
  overlappingExtractions,
  focusedExtractions,
  hoveredExtractions,
  setFocusedExtraction,
  setHoveredExtraction,
  removeFocusedExtraction,
  removeHoveredExtraction,
  hideFill,
  documentFileContainerRef,
}: OverlappingExtractionGroupHighlightProps) => {
  const highlightRefs = useRef([]);
  const extractionWithLargestText = maxBy(
    (e) => boundingBoxArea(e.result.boundingBox),
    overlappingExtractions,
  );
  const overlappingExtractionsAreHovered = extractionsMatchFocusedExtractions(
    hoveredExtractions,
    overlappingExtractions,
  );
  const overlappingExtractionsAreFocused = extractionsMatchFocusedExtractions(
    focusedExtractions,
    overlappingExtractions,
  );
  const setFocusedForAllOverlappingExtractions = () => {
    setFocusedExtraction(
      overlappingExtractions.map((extraction) => ({
        extractionGroup: extraction.extractionGroup,
        focusedBoundingBoxId: extraction.result.id,
      })),
    );
  };
  const setHoveredForAllOverlappingExtractions = () => {
    setHoveredExtraction(
      overlappingExtractions.map((extraction) => ({
        extractionGroup: extraction.extractionGroup,
        focusedBoundingBoxId: extraction.result.id,
      })),
    );
  };
  const overlappingExtractionHoverCard = (
    <OverlappingExtractionHoverCard
      extractions={overlappingExtractions}
      setFocusedExtractions={setFocusedExtraction}
    />
  );

  return (
    <>
      {overlappingExtractions.map((extraction, i) => {
        const { left, right, top, bottom } = extraction.result.boundingBox;
        const matchRquirementIsSatisfied = matchLevelSatisfies(
          extraction.extractionGroup.extractionMatchLevel,
          extraction.extractionGroup.matchLevelMinimum,
        );
        const isExtractionWithLargestTextInOverlappinGroup = isEqual(
          extractionWithLargestText,
          extraction,
        );
        const extractionGroupIsFocused = focusedExtractions.some((focusedExtraction) =>
          extractionsMatchFocusedExtractions([focusedExtraction], [extraction]),
        );
        const onlySingletonExtractionIsFocused = extractionsMatchFocusedExtractions(
          focusedExtractions,
          [extraction],
        );
        const onlySingletonExtractionIsHovered = extractionsMatchFocusedExtractions(
          hoveredExtractions,
          [extraction],
        );
        let extractionHoverCard: ReactElement | null = null;
        if (onlySingletonExtractionIsFocused || onlySingletonExtractionIsHovered) {
          extractionHoverCard = (
            <ExtractionHoverCard
              extraction={extraction}
              focusedExtractions={focusedExtractions}
              setFocusedExtractions={setFocusedExtraction}
            />
          );
        } else if (focusedExtractions.length > 1 || hoveredExtractions.length > 1) {
          extractionHoverCard = overlappingExtractionHoverCard;
        }
        const highlight = (
          <HighlightAnnotation
            id={getExtractionHighlightId(extraction.extractionGroup, extraction.result.id)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            ref={(ref) => (highlightRefs.current[i] = ref)}
            color={
              extraction.extractionGroup.extractionType === ExtractionType.Unguided
                ? ExtractionColor.Unguided
                : matchRquirementIsSatisfied
                  ? ExtractionColor.GuidedSatisfied
                  : ExtractionColor.GuidedUnsatisfied
            }
            // extraction highlights are filled by default if no extractions are focused
            fillHighlight={
              hideFill ? false : focusedExtractions.length === 0 || extractionGroupIsFocused
            }
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onClick={!onlySingletonExtractionIsFocused && setFocusedForAllOverlappingExtractions}
            onMouseEnter={setHoveredForAllOverlappingExtractions}
            onMouseLeave={removeHoveredExtraction}
            style={{
              position: 'absolute',
              top: `${top * 100}%`,
              left: `${left * 100}%`,
              height: `${(bottom - top) * 100}%`,
              width: `${(right - left) * 100}%`,
            }}
          />
        );
        return (
          <ThemeProvider
            theme={defaultTheme}
            key={`document-highlight-${extraction.extractionGroup.fieldName}-${extraction.result.id}`}
          >
            <Tooltip
              trigger={highlight}
              open={
                ((overlappingExtractionsAreHovered || overlappingExtractionsAreFocused) &&
                  // to prevent multiple tooltips showing up
                  isExtractionWithLargestTextInOverlappinGroup) ||
                onlySingletonExtractionIsFocused
              }
              side='right'
              hideArrow
              collisionBoundary={documentFileContainerRef.current}
              onEscapeKeyDown={removeFocusedExtraction}
              onPointerDownOutside={(e) =>
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                !highlightRefs.current.some((ref) => ref.contains(e.target as Node)) &&
                documentFileContainerRef.current?.contains(e.target as Node) &&
                removeFocusedExtraction()
              }
              hideWhenDetached
              style={{ padding: 0 }}
            >
              {extractionHoverCard}
            </Tooltip>
          </ThemeProvider>
        );
      })}
    </>
  );
};

interface ExtractionHighlightsProps {
  extractions: DocumentViewerSingletonExtraction[];
  focusedExtractions: FocusedExtraction[];
  setFocusedExtraction: (focusedExtractions: FocusedExtraction[]) => void;
  removeFocusedExtraction: () => void;
  hoveredExtractions: FocusedExtraction[];
  setHoveredExtraction: (hoveredExtractions: FocusedExtraction[]) => void;
  removeHoveredExtraction: () => void;
  hideFill?: boolean;
  documentFileContainerRef: MutableRefObject<HTMLDivElement>;
}

const ExtractionHighlights = ({
  extractions,
  focusedExtractions,
  setFocusedExtraction,
  removeFocusedExtraction,
  hoveredExtractions,
  setHoveredExtraction,
  removeHoveredExtraction,
  documentFileContainerRef,
  hideFill = false,
}: ExtractionHighlightsProps) => {
  const groupedOverlappingExtractions: DocumentViewerSingletonExtraction[][] = useMemo(() => {
    extractions.sort((e1, e2) => {
      // extractions with multiple results have been flattened to multiple extractions with a single result
      const { left: e1Left, right: e1Right, top: e1Top, bottom: e1Bottom } = e1.result.boundingBox;
      const { left: e2Left, right: e2Right, top: e2Top, bottom: e2Bottom } = e2.result.boundingBox;
      return e1Top - e2Top || e1Left - e2Left || e1Bottom - e2Bottom || e1Right - e2Right;
    });
    return extractions.reduce(
      (overlapGroups, x) => {
        // we only need to check the last group since we already sorted the extractions from top to bottom and left to right
        const lastOverlapGroup = overlapGroups[overlapGroups.length - 1];
        if (lastOverlapGroup.length === 0) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lastOverlapGroup.push(x);
          return overlapGroups;
        }
        const overlapsWithCurrentGroup = lastOverlapGroup.reduce(
          (hasOverlap, y) =>
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            hasOverlap || boundingBoxesOverlap(y.result.boundingBox, x.result.boundingBox),
          false,
        );

        if (overlapsWithCurrentGroup) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lastOverlapGroup.push(x);
        } else {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          overlapGroups.push([x]);
        }
        return overlapGroups;
      },
      [[]],
    );
  }, [extractions]);

  return (
    <>
      {groupedOverlappingExtractions.map((overlappingExtractions, i) => (
        <OverlappingExtractionGroupHighlight
          key={`document-viewer__overlapping-extractions-group-highlight--${i}`}
          overlappingExtractions={overlappingExtractions}
          focusedExtractions={focusedExtractions}
          hoveredExtractions={hoveredExtractions}
          setFocusedExtraction={setFocusedExtraction}
          setHoveredExtraction={setHoveredExtraction}
          removeFocusedExtraction={removeFocusedExtraction}
          removeHoveredExtraction={removeHoveredExtraction}
          documentFileContainerRef={documentFileContainerRef}
          hideFill={hideFill}
        />
      ))}
    </>
  );
};

export default ExtractionHighlights;
